import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import { FormikControl } from '..';
import { signUpInitialValues as initialValues, signUpSchema as validationSchema, SignUpInfoCard } from '.';
import {
    FormSuccess,
    FormFailed,
    FormSubmitting,
    AttributionSurvey,
    ConsentMsg,
    SmsOptInCheckbox,
    SMS_CONSENT_LEGAL_TERMS,
} from '../shared';
import { Modal } from 'react-bootstrap';
import {
    NETLIFY_FUNCTIONS_PATH,
    getFacebookCookies,
    getOptimizelyData,
    validateWithPossibleNow,
    syncPardotTracking,
    getLeadsCaptureEcho,
    isLeadsCaptureResponse,
} from '../../../utils';
import { navigate } from 'gatsby';
import axios from 'axios';
import { useScrollToAnchor } from '../../../hooks/useScrollToAnchor.ts';
import { readStoredCloudampData } from '../../../hooks/useCloudampData';

export function SignUpForm({ siteData, showSignUpModal, setShowSignUpModal }) {
    initialValues.location = siteData?.currentMetro.stores[0].salesforceValue;

    const [showAttributionSurvey, setShowAttributionSurvey] = useState(false);
    // State to hold form values for use in attribution survey
    const [formValues, setFormValues] = useState(null);
    const [smsOptIn, setSmsOptIn] = useState(false);
    const { scrollToAnchor } = useScrollToAnchor({ offset: 300 });

    const handleSubmit = async (values, helpers) => {
        scrollToAnchor('signup-modal');
        helpers.setSubmitting(true);
        const { fbp, fbc } = getFacebookCookies();
        const { experiment_exposures, experiment_user_ID } = getOptimizelyData();
        try {
            // Validate phone and email with PossibleNow
            const { phoneFound, wireless, emailFound, result, flags } = await validateWithPossibleNow(
                values.phone,
                values.email
            );

            const formData = {
                ...values,
                updates: true,
                // Checked => the user opted IN to marketing SMS, so the opt-OUT flag is false.
                marketing_sms_optout: !smsOptIn,
                cloudamp__data__c: readStoredCloudampData(),
                formType: 'newsletter',
                fbp,
                fbc,
                experiment_exposures,
                experiment_user_ID,
                phoneFound,
                wireless,
                emailFound,
                result,
                flags,
            };

            const response = await axios.post(`${NETLIFY_FUNCTIONS_PATH}form-handler`, formData);

            // Stored after the POST so the survey can echo the ids the handler returned. On the
            // Pardot path getLeadsCaptureEcho yields {} and these values stay as they were.
            setFormValues({ ...formData, ...getLeadsCaptureEcho(response) });

            // On the leads-capture path the lead is already in Salesforce; syncing a Pardot
            // prospect on top of it would double-write.
            if (!isLeadsCaptureResponse(response)) syncPardotTracking(formData);

            // Show attribution survey instead of immediate success
            setShowAttributionSurvey(true);

            helpers.setSubmitting(false);
            helpers.setStatus('Success');

            navigate('?signup-success'); // This is for Marketing
            helpers.resetForm();
            // smsOptIn lives outside Formik; reset it too so the express-consent box never carries over.
            setSmsOptIn(false);
        } catch (error) {
            console.error('Form submission error:', error);
            helpers.setSubmitting(false);
            helpers.setStatus('Failed');
            setFormValues(null);
            setShowAttributionSurvey(false);
        }
    };

    const handleAttributionComplete = () => {
        setShowAttributionSurvey(false);
        setTimeout(() => {
            setFormValues(null);
        }, 10000); // Clear form values after 10 second
    };

    return (
        <Modal
            centered
            id="signup-modal"
            show={showSignUpModal}
            onHide={() => setShowSignUpModal(false)}
            aria-labelledby="signup-modal-title"
            contentClassName="milan-bg-white"
            className={formValues && showAttributionSurvey ? 'modal-lg' : ''}
        >
            {!formValues && !showAttributionSurvey && (
                <Modal.Header className="border-bottom-0 py-0">
                    <Modal.Title id="signup-modal-title">
                        <h3 className="subhead-sm mb-0 mt-4 milan-text-primary">Get Milan Laser Updates</h3>
                    </Modal.Title>
                </Modal.Header>
            )}
            <Modal.Body className="mb-2">
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    validateOnChange={false}
                >
                    {(formik) => {
                        if (formik.isSubmitting) {
                            return <FormSubmitting />;
                        }

                        // Handle error state
                        if (formik.status === 'Failed') {
                            return <FormFailed />;
                        }

                        if (formValues) {
                            // At this point the form is submitted
                            if (showAttributionSurvey) {
                                return (
                                    <AttributionSurvey
                                        formValues={formValues}
                                        onComplete={handleAttributionComplete}
                                        InfoCard={SignUpInfoCard}
                                    />
                                );
                            }
                            return <FormSuccess />;
                        }

                        return (
                            <Form className="w-100">
                                <div className="row mb-3 text-start gx-3">
                                    <div className="col-12 col-sm-6 mb-3 mb-sm-0">
                                        <FormikControl
                                            control="input"
                                            name="first_name"
                                            placeholder="First Name"
                                            type="text"
                                            required
                                            role="input"
                                        />
                                    </div>
                                    <div className="col-12 col-sm-6">
                                        <FormikControl
                                            control="input"
                                            name="last_name"
                                            placeholder="Last Name"
                                            type="text"
                                            required
                                            role="input"
                                        />
                                    </div>
                                </div>
                                <div className="mb-3 text-start">
                                    <FormikControl
                                        control="emailInput"
                                        name="email"
                                        placeholder="Your Email"
                                        required
                                        role="input"
                                    />
                                </div>
                                <div className="text-start">
                                    <FormikControl
                                        control="phoneInput"
                                        name="phone"
                                        placeholder="Phone Number"
                                        type="tel"
                                        required
                                        role="input"
                                    />
                                </div>
                                {siteData?.currentMetro.stores.length > 1 && (
                                    <div className="mt-3">
                                        <FormikControl
                                            control="selectLocation"
                                            name="location"
                                            label="Location"
                                            milanStores={siteData.milanStores}
                                        />
                                    </div>
                                )}
                                <div className="row justify-content-center my-3">
                                    <div className="col-12 lh-1">
                                        <SmsOptInCheckbox
                                            checked={smsOptIn}
                                            onChange={(e) => setSmsOptIn(e.target.checked)}
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-12 text-center">
                                        <button
                                            className="milan-btn milan-red-btn w-100"
                                            type="submit"
                                            disabled={!formik.isValid || formik.isSubmitting}
                                        >
                                            {formik.isSubmitting ? (
                                                'Submitting...'
                                            ) : (
                                                <>
                                                    Submit <sup>*</sup>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                                <div className="row justify-content-center mt-3">
                                    <div className="col-12 lh-1">
                                        {/* separator '. ' closes the Terms link before SMS_CONSENT_LEGAL_TERMS opens a new sentence */}
                                        <ConsentMsg
                                            cta="Submit<sup>*</sup>"
                                            legalTerms={SMS_CONSENT_LEGAL_TERMS}
                                            separator=". "
                                            termsLabel="Terms"
                                        />
                                    </div>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            </Modal.Body>
        </Modal>
    );
}

export default SignUpForm;
